import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Copy, Check } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import api from '../../services/api';
import { useQuery } from '@tanstack/react-query';

const schema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  magalu_id: z.string().min(1, 'Magalu ID é obrigatório'),
  ambiente_id: z.string().min(1, 'Ambiente é obrigatório'),
  regiao: z.string().min(1, 'Região é obrigatória'),
  start_time: z.string().optional(),
  stop_time: z.string().optional(),
  weekdays: z.array(z.string()).optional(),
  ativo: z.boolean().default(true),
  snapshot_time: z.string().optional(),
  snapshot_weekdays: z.array(z.string()).optional(),
  snapshot_retention_limit: z
    .union([
      z.literal(''),
      z.coerce.number().int('Deve ser um número inteiro').min(1, 'Mínimo 1'),
    ])
    .optional(),
});

type FormData = z.infer<typeof schema>;
type Ambiente = any;
type VM = any;

const regioes = [
  { value: 'br_se1', label: 'BR-SE1' },
  { value: 'br_mgl1', label: 'BR-MGL1' },
  { value: 'br_ne1', label: 'BR-NE1' },
];

const weekdayOptions = [
  { value: 'mon', label: 'Seg' },
  { value: 'tue', label: 'Ter' },
  { value: 'wed', label: 'Qua' },
  { value: 'thu', label: 'Qui' },
  { value: 'fri', label: 'Sex' },
  { value: 'sat', label: 'Sáb' },
  { value: 'sun', label: 'Dom' },
];

export const VMForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditing = Boolean(id);
  const { addToast } = useToast();

  const fetchAmbientes = async () => {
    try {
      const response = await api.get<Ambiente[]>('/api/ambientes');
      return response.data;
    } catch (error: any) {
      console.error('Error fetching ambientes:', error);
      addToast({
        type: 'error',
        title: 'Erro ao carregar ambientes',
        description: error.response?.data?.error || error.message
      });
      throw error;
    }
  };
  const { data: ambientesData, isLoading: isLoadingAmbientes, error: errorAmbientes } = useQuery({
    queryKey: ['ambientes'],
    queryFn: fetchAmbientes
  });
  const ambientes = ambientesData || [];

  const fetchVM = async (vmId: string) => {
    try {
      const response = await api.get<VM>(`/api/vms/${vmId}`);
      const vmData = response.data;
      if (!vmData) throw new Error('VM não encontrada ou resposta inválida');
      return vmData;
    } catch (error: any) {
      console.error('Error fetching VM:', error);
      addToast({
        type: 'error',
        title: 'Erro ao carregar VM',
        description: error.response?.data?.error || error.message
      });
      throw error;
    }
  };
  const { data: vmData, isLoading: isLoadingVM, error: errorVM } = useQuery({
    queryKey: ['vm', id],
    queryFn: () => fetchVM(id!),
    enabled: isEditing
  });
  const vm = vmData;

  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const handleCopyText = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      addToast({ type: 'success', title: 'Copiado com sucesso!' });
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      addToast({ type: 'error', title: 'Erro ao copiar' });
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      ativo: true,
      weekdays: [],
      snapshot_time: '',
      snapshot_weekdays: [],
      snapshot_retention_limit: '',
    },
  });

  const selectedWeekdays = watch('weekdays') || [];
  const selectedSnapshotWeekdays = watch('snapshot_weekdays') || [];

  const isLoading = isLoadingAmbientes || (isEditing && isLoadingVM);
  const queryError = errorAmbientes || (isEditing && errorVM);

  useEffect(() => {
    if (isEditing && vm) {
      reset({
        nome: vm.nome || '',
        magalu_id: vm.magalu_id || '',
        ambiente_id: vm.ambiente_id?.toString() || '',
        regiao: vm.regiao || '',
        ativo: vm.ativo ?? true,
        start_time: vm.start_time || '',
        stop_time: vm.stop_time || '',
        weekdays: vm.weekdays || [],
        snapshot_time: vm.snapshot_time || '',
        snapshot_weekdays: vm.snapshot_weekdays || [],
        snapshot_retention_limit: vm.snapshot_retention_limit ?? '',
      });
    } else if (!isEditing) {
      reset({
        nome: '',
        magalu_id: '',
        ambiente_id: '',
        regiao: '',
        ativo: true,
        start_time: '',
        stop_time: '',
        weekdays: [],
        snapshot_time: '',
        snapshot_weekdays: [],
        snapshot_retention_limit: '',
      });
    }
  }, [isEditing, vm, reset]);

  const handleWeekdayToggle = (day: string) => {
    const currentWeekdays = watch('weekdays') || [];
    const newWeekdays = currentWeekdays.includes(day)
      ? currentWeekdays.filter(d => d !== day)
      : [...currentWeekdays, day];
    setValue('weekdays', newWeekdays);
  };

  const handleSnapshotWeekdayToggle = (day: string) => {
    const currentWeekdays = watch('snapshot_weekdays') || [];
    const newWeekdays = currentWeekdays.includes(day)
      ? currentWeekdays.filter(d => d !== day)
      : [...currentWeekdays, day];
    setValue('snapshot_weekdays', newWeekdays);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('pt-BR');
    } catch (error) {
      return '-';
    }
  };

  const getStateLabel = (state: string) => {
    const labels: Record<string, string> = {
      'running': 'Em execução',
      'stopped': 'Parada',
      'starting': 'Iniciando',
      'stopping': 'Parando',
      'unknown': 'Desconhecido'
    };
    return labels[state] || state;
  };

  const getStateColor = (state: string) => {
    const colors: Record<string, string> = {
      'running': 'text-green-600',
      'stopped': 'text-red-600',
      'starting': 'text-yellow-600',
      'stopping': 'text-yellow-600',
      'unknown': 'text-gray-600'
    };
    return colors[state] || 'text-gray-600';
  };

  const onSubmit = async (data: FormData) => {
    const url = isEditing ? `/api/vms/${id}` : '/api/vms';
    const method = isEditing ? 'put' : 'post';

    const retentionLimit =
      data.snapshot_retention_limit === '' || data.snapshot_retention_limit === undefined
        ? null
        : Number(data.snapshot_retention_limit);

    const payload = isEditing
      ? {
          start_time: data.start_time || null,
          stop_time: data.stop_time || null,
          weekdays: data.weekdays,
          ativo: data.ativo,
          snapshot_time: data.snapshot_time || null,
          snapshot_weekdays: data.snapshot_weekdays,
          snapshot_retention_limit: retentionLimit,
        }
      : { ...data, snapshot_retention_limit: retentionLimit };

    try {
      let response;
      if (method === 'put') {
        response = await api.put(url, payload);
      } else {
        response = await api.post(url, payload);
      }

      if (response.status === 200 || response.status === 201) {
        addToast({
          type: 'success',
          title: `VM ${isEditing ? 'atualizada' : 'criada'} com sucesso!`
        });
        navigate('/vms');
      } else {
        console.error('Falha ao salvar VM, status:', response.status, response.data);
        const errorDesc = response.data?.error || `Status: ${response.status}`;
        addToast({ type: 'error', title: 'Erro ao salvar VM', description: errorDesc });
        throw new Error(errorDesc || 'Falha ao salvar VM');
      }
    } catch (error: any) {
      console.error('Error saving VM:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido ao salvar.';
      addToast({
        type: 'error',
        title: 'Erro ao salvar VM',
        description: errorMessage
      });
    }
  };

  if (queryError) {
    return <div className="text-center p-4 text-red-600">Erro ao carregar dados necessários. Tente novamente.</div>;
  }

  if (isLoading) {
    return <div className="text-center p-4">Carregando dados...</div>;
  }

  return (
    <div>
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/vms')}
          className="mr-4 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-2xl font-bold mb-6">{id ? 'Editar VM' : 'Nova VM'}</h1>
      </div>

      {isEditing && vm && (
         <div className="mb-6 p-4 bg-gray-100 rounded-lg text-sm text-gray-700 grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-4">
            <div><strong>Nome:</strong> {vm.nome}</div>
            <div><strong>Magalu ID:</strong> {vm.magalu_id}</div>
            <div><strong>Ambiente:</strong> {Array.isArray(ambientes) ? ambientes.find(a => a.id === vm.ambiente_id)?.nome : '-'}</div>
            <div><strong>Região:</strong> {Array.isArray(regioes) ? regioes.find(r => r.value === vm.regiao)?.label : vm.regiao || '-'}</div>
            <div>
                <strong>Estado Atual:</strong> 
                <span className={`ml-1 font-medium ${getStateColor(vm.state || 'unknown')}`}>
                  {getStateLabel(vm.state || 'unknown')}
                </span>
            </div>
            <div><strong>Criado em:</strong> {formatDate(vm.createdAt)}</div>
            <div><strong>Atualizado em:</strong> {formatDate(vm.updatedAt)}</div>
            <div className="flex items-center gap-1">
              <strong>IP Privado:</strong> {vm.private_ip || '-'}
              {vm.private_ip && (
                <button
                  type="button"
                  onClick={() => handleCopyText(vm.private_ip, 'private_ip')}
                  className="text-gray-400 hover:text-gray-600"
                  title={`Copiar IP privado: ${vm.private_ip}`}
                >
                  {copiedKey === 'private_ip' ? (
                    <Check className="w-3 h-3 text-green-600" />
                  ) : (
                    <Copy className="w-3 h-3" />
                  )}
                </button>
              )}
            </div>
            <div className="flex items-center gap-1">
              <strong>IP Público:</strong> {vm.public_ip || '-'}
              {vm.public_ip && (
                <button
                  type="button"
                  onClick={() => handleCopyText(vm.public_ip, 'public_ip')}
                  className="text-gray-400 hover:text-gray-600"
                  title={`Copiar IP público: ${vm.public_ip}`}
                >
                  {copiedKey === 'public_ip' ? (
                    <Check className="w-3 h-3 text-green-600" />
                  ) : (
                    <Copy className="w-3 h-3" />
                  )}
                </button>
              )}
            </div>
         </div>
      )}

      {isEditing && vm && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg text-sm text-gray-700 grid grid-cols-1 md:grid-cols-5 gap-x-6 gap-y-4 border border-blue-200">
            <h3 className="text-lg font-semibold text-blue-800 col-span-full mb-2">Detalhes do Tipo da VM</h3>
            <div><strong>Tipo:</strong> {vm.tipoVm?.name || '-'}</div>
            <div><strong>CPU:</strong> {vm.tipoVm?.vcpus || '-'} vCPUs</div>
            <div><strong>RAM:</strong> {vm.tipoVm?.ram ? `${(vm.tipoVm.ram / 1024).toFixed(1)} GB` : '-'}</div>
            <div><strong>Disco:</strong> {vm.tipoVm?.disk ? `${vm.tipoVm.disk} GB` : '-'}</div>
            <div><strong>GPU:</strong> {vm.tipoVm?.gpu || 'N/A'}</div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 bg-white p-6 rounded-lg shadow">
        {!isEditing && (
          <>
            <div>
              <label htmlFor="nome" className="block text-sm font-medium text-gray-700">
                Nome <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="nome"
                {...register('nome')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                disabled={isLoading || isSubmitting}
              />
              {errors.nome && <p className="mt-1 text-sm text-red-600">{errors.nome.message}</p>}
            </div>

            <div>
              <label htmlFor="magalu_id" className="block text-sm font-medium text-gray-700">
                Magalu ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="magalu_id"
                {...register('magalu_id')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                disabled={isLoading || isSubmitting}
              />
              {errors.magalu_id && <p className="mt-1 text-sm text-red-600">{errors.magalu_id.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="ambiente_id" className="block text-sm font-medium text-gray-700">
                  Ambiente <span className="text-red-500">*</span>
                </label>
                <select
                  id="ambiente_id"
                  {...register('ambiente_id')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  disabled={isLoading || isSubmitting}
                >
                  <option value="">Selecione um ambiente</option>
                  {Array.isArray(ambientes) && ambientes.map((ambiente) => (
                    <option key={ambiente.id} value={ambiente.id}>
                      {ambiente.nome}
                    </option>
                  ))}
                </select>
                {errors.ambiente_id && <p className="mt-1 text-sm text-red-600">{errors.ambiente_id.message}</p>}
              </div>

              <div>
                <label htmlFor="regiao" className="block text-sm font-medium text-gray-700">
                  Região <span className="text-red-500">*</span>
                </label>
                <select
                  id="regiao"
                  {...register('regiao')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  disabled={isLoading || isSubmitting}
                >
                  <option value="">Selecione uma região</option>
                  {Array.isArray(regioes) && regioes.map((regiao) => (
                    <option key={regiao.value} value={regiao.value}>
                      {regiao.label}
                    </option>
                  ))}
                </select>
                {errors.regiao && <p className="mt-1 text-sm text-red-600">{errors.regiao.message}</p>}
              </div>
            </div>
          </>
        )}

        {/* Container Grid para as duas seções de agendamento */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6 border-t pt-6">

          {/* Seção de Agendamento Início/Parada */}
          <fieldset className="border-gray-200 pt-0">
             <legend className="text-lg font-medium text-gray-900 mb-4">Agendamento Início/Parada</legend>
            {/* Div para Horários Start/Stop */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="start_time" className="block text-sm font-medium text-gray-700">
                  Horário de Início
                </label>
                <input
                  type="time"
                  id="start_time"
                  {...register('start_time')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  disabled={isLoading || isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="stop_time" className="block text-sm font-medium text-gray-700">
                  Horário de Parada
                </label>
                <input
                  type="time"
                  id="stop_time"
                  {...register('stop_time')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  disabled={isLoading || isSubmitting}
                />
              </div>
            </div>
            {/* Div para Dias da Semana e Ativo */}
            <div className="mb-2">
                <div className="flex justify-between items-center mb-2">
                     <label className="block text-sm font-medium text-gray-700">
                       Dias da Semana
                     </label>
                 </div>
                <div className="grid grid-cols-4 md:grid-cols-4 lg:grid-cols-7 gap-2">{/* Ajuste grid dias */}
                  {weekdayOptions.map((day) => (
                    <label key={day.value} className="flex items-center p-2 border rounded-md hover:bg-gray-50">
                      <input
                        type="checkbox"
                        value={day.value}
                        checked={selectedWeekdays.includes(day.value)}
                        onChange={() => handleWeekdayToggle(day.value)}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:ring-blue-500"
                        disabled={isLoading || isSubmitting}
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {day.label}
                      </span>
                    </label>
                  ))}
                </div>
            </div>
          </fieldset>

          {/* Seção de Agendamento de Snapshot */}
          <fieldset className="border-gray-200 pt-0 md:border-l md:pl-6">
             <legend className="text-lg font-medium text-gray-900 mb-4">Agendamento de Snapshot da VM</legend>
            {/* Div para Horário do Snapshot */}
            <div className="mb-4">
              <label htmlFor="snapshot_time" className="block text-sm font-medium text-gray-700">
                Horário do Snapshot
              </label>
              <input
                type="time"
                id="snapshot_time"
                {...register('snapshot_time')}
                className="mt-1 block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                disabled={isLoading || isSubmitting}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="snapshot_retention_limit" className="block text-sm font-medium text-gray-700">
                Retenção de snapshots (quantidade)
              </label>
              <input
                type="number"
                id="snapshot_retention_limit"
                min={1}
                placeholder="Sem limite"
                {...register('snapshot_retention_limit')}
                className="mt-1 block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                disabled={isLoading || isSubmitting}
              />
              <p className="mt-1 text-xs text-gray-500">
                Opcional. Apenas snapshots criados pelo agendamento deste sistema. Os mais antigos são removidos após o sync horário.
              </p>
              {errors.snapshot_retention_limit && (
                <p className="mt-1 text-sm text-red-600">{errors.snapshot_retention_limit.message}</p>
              )}
            </div>
            {/* Div para Dias da Semana do Snapshot */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dias da Semana
              </label>
              <div className="grid grid-cols-4 md:grid-cols-4 lg:grid-cols-7 gap-2"> {/* Mantém o grid apenas para os dias */}
                {weekdayOptions.map((day) => (
                  <label key={`${day.value}-snapshot`} className="flex items-center p-2 border rounded-md hover:bg-gray-50">
                    <input
                      type="checkbox"
                      value={day.value}
                      checked={selectedSnapshotWeekdays.includes(day.value)}
                      onChange={() => handleSnapshotWeekdayToggle(day.value)}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:ring-blue-500"
                      disabled={isLoading || isSubmitting}
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {day.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </fieldset>

        </div> {/* Fim do container Grid */}

        {/* Checkbox Agendamento Ativo movido para o final */}
        <div className="border-t pt-6">
          <label className="flex items-center">
              <input
                type="checkbox"
                {...register('ativo')}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                disabled={isLoading || isSubmitting}
              />
              <span className="ml-2 text-sm text-gray-700">Agendamento Início/Parada Ativo</span>
            </label>
        </div>

        <div className="flex justify-end space-x-4 border-t pt-6 mt-6">
          <button
            type="button"
            onClick={() => navigate('/vms')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
             disabled={isLoading || isSubmitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isLoading || isSubmitting}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
};